<template>
  <div class="dynamic-form">
    <p-progress-linear v-if="isLoading" indeterminate></p-progress-linear>
    <p-form :model="formPayload">
      <p-dynamic-form
        v-if="!isLoading && _.isEmpty(errorResponse) && !isSubmitted"
        v-model="formPayload"
        :summary.sync="formSummary"
        :json-schema.sync="formPayload.schema"
        :form-definition="formDefinition"
        :skip-request-summary-email="innerSkipRequestSummaryEmail"
      ></p-dynamic-form>
    </p-form>
    <p-request-alert v-if="_.isSet(errorResponse)" v-model="errorResponse" class="mb-3 mt-3"></p-request-alert>
    <p-alert type="success" class="mb-3 mt-3" :value="isShowSubmitted">
      {{ $t('core.page.dynamicForm.message.dynamicFormWasSentSuccessfully') }}
    </p-alert>
    <p-row>
      <p-col v-if="!isSubmitted" justify-end>
        <p-button :disabled="!canPostForm" :loading="isSubmitting" @click="postForm">
          {{ $t('core.app.send') }}
        </p-button>
      </p-col>
      <p-col v-else justify-start>
        <p-button @click="windowBack">
          {{ $t('core.app.back') }}
        </p-button>
      </p-col>
    </p-row>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import DynamicFormFieldDefinitionResourceViewModel from '@glittr/frontend-core/src/services/v1/viewModel/resource/DynamicFormDefinitionResourceViewModel';
  import GetDynamicFormDefinitionRequestViewModel from '@glittr/frontend-core/src/services/v1/viewModel/request/DynamicForm/GetDynamicFormDefinitionRequestViewModel';
  import DynamicFormPostRequestViewModel from '@glittr/frontend-core/src/services/v1/viewModel/resource/DynamicFormPostRequestViewModel';
  import DynamicFormPayloadResourceViewModel from '@glittr/frontend-core/src/services/v1/viewModel/resource/DynamicFormPayloadResourceViewModel';
  import DynamicFormContactResourceViewModel from '@glittr/frontend-core/src/services/v1/viewModel/resource/DynamicFormContactResourceViewModel';
  import GetDynamicFormPayloadRequestModel from '@/src/services/v2/model/get-dynamic-form-payload-request-model';

  export default Vue.extend({
    name: 'UpdateMemberData',
    layout: 'layout-widget',
    props: {
      contactId: { default: undefined, type: String },
      definitionId: { default: undefined, type: String },
      businessUnit: { default: undefined, type: Number },
      appSystemKey: { default: undefined, type: String },
      addressOwner: { default: undefined, type: Number },
      submitRedirectUrl: { default: undefined, type: String },
      errorElementId: { default: undefined, type: String },
      successElementId: { default: undefined, type: String },
      skipRequestSummaryEmail: { default: undefined, type: Boolean },
    },
    data: () => ({
      formSummary: {},
      isLoading: true,
      isSubmitting: false,
      errorResponse: undefined as any,
      formDefinition: new DynamicFormFieldDefinitionResourceViewModel(),
      formPayload: new DynamicFormPayloadResourceViewModel().fromModel({
        fields: [],
        contact: new DynamicFormContactResourceViewModel(),
        skipRequestSummaryEmail: false,
      }),
      isSubmitted: false,
      isShowSubmitted: false,
    }),
    computed: {
      canPostForm(): boolean {
        return !this.isSubmitting && this.formDefinition.initalized;
      },
      innerSubmitRedirectUrl(): string {
        if (this._.isSet(this.submitRedirectUrl)) {
          return this.submitRedirectUrl;
        }
        if (this.$config.values['widget-submit-redirect-url']) {
          return this.$config.values['widget-submit-redirect-url'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.submitRedirectUrl as string;
      },
      innerAppSystemKey(): string {
        if (this._.isSet(this.appSystemKey)) {
          return this.appSystemKey;
        }
        if (this.$config.values['widget-dynamicform-appsystemkey']) {
          return this.$config.values['widget-dynamicform-appsystemkey'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.appsystemkey as string;
      },
      innerSuccessElementId(): string | undefined {
        if (this._.isSet(this.successElementId)) {
          return this.successElementId;
        }
        if (this.$config.values['widget-dynamicform-display-success-elementid']) {
          return this.$config.values['widget-dynamicform-display-success-elementid'];
        }
        return undefined;
      },
      innerErrorElementId(): string | undefined {
        if (this._.isSet(this.errorElementId)) {
          return this.errorElementId;
        }
        if (this.$config.values['widget-dynamicform-display-error-elementid']) {
          return this.$config.values['widget-dynamicform-display-error-elementid'];
        }
        return undefined;
      },
      innerContactId(): string {
        if (this._.isSet(this.contactId)) {
          return this.contactId;
        }
        if (this.$config.values['widget-dynamicform-contactid']) {
          return this.$config.values['widget-dynamicform-contactid'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.contactid as string;
      },
      innerBusinessUnit(): number | undefined {
        if (this._.isSet(this.businessUnit)) {
          return this.businessUnit;
        }
        if (this.$config.values['widget-dynamicform-businessunit']) {
          return this.$config.values['widget-dynamicform-businessunit'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.businessunit ? +queries.businessunit : undefined;
      },
      innerAddressOwner(): number | undefined {
        if (this._.isSet(this.addressOwner)) {
          return this.addressOwner;
        }
        if (this.$config.values['widget-dynamicform-addressownernumber']) {
          return this.$config.values['widget-dynamicform-addressownernumber'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.addressOwner ? +queries.addressOwner : undefined;
      },
      innerDefinitionId(): string {
        if (this._.isSet(this.definitionId)) {
          return this.definitionId;
        }
        if (this.$config.values['widget-dynamicform-formid']) {
          return this.$config.values['widget-dynamicform-formid'];
        }
        const queries = this.$routerUtils.getQueryParams();
        return queries.id as string;
      },
      innerSkipRequestSummaryEmail(): boolean {
        if (this._.isSet(this.skipRequestSummaryEmail)) {
          return this.skipRequestSummaryEmail;
        }
        if (this.$config.values['widget-dynamicform-skiprequestsummaryemail']) {
          return this.$config.values['widget-dynamicform-skiprequestsummaryemail'];
        }
        return false;
      },
    },
    async mounted() {
      await this.loadFormDefinition();
      await this.initializeFormPayload();
    },
    methods: {
      windowBack() {
        let backFunc = this.$router.back.bind(this.$router);
        if (this._.isSet(window?.history)) {
          backFunc = window.history.back.bind(window.history);
        } else if (typeof (window as any)?.back === 'function') {
          backFunc = (window as any).back.bind(window);
        }
        backFunc();
      },
      async postForm() {
        this.isSubmitting = true;
        try {
          if (this.innerErrorElementId !== undefined && this.innerErrorElementId !== '') {
            const el = document.getElementById(this.innerErrorElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerErrorElementId}' not found.`);
            } else {
              el.classList.remove('glitWidgetElementVisible');
            }
          }
          if (!this.formPayload.validate()) {
            this.isSubmitting = false;
            return;
          }
          this.formPayload.definitionId = this.formDefinition?.id!;
          this.formPayload.languageIso2 = this.$translation.get() ?? this.$config.values['language-default'];
          this.formPayload.addressOwnerNumber = this.innerAddressOwner;
          this.formPayload.businessUnit = this.innerBusinessUnit;
          this.formPayload.skipRequestSummaryEmail = this.innerSkipRequestSummaryEmail;
          const req = new DynamicFormPostRequestViewModel().fromModel({
            appSystemKey: this.innerAppSystemKey,
            form: this.formPayload,
          });
          await this.$service.api.core.dynamicForm.postDynamicForm(req);
          this.isSubmitted = true;
          if (this.innerSuccessElementId !== undefined && this.innerSuccessElementId !== '') {
            const el = document.getElementById(this.innerSuccessElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerSuccessElementId}' not found.`);
            } else {
              el.classList.add('glitWidgetElementVisible');
              return;
            }
          } else if (this.innerSubmitRedirectUrl !== undefined && this.innerSubmitRedirectUrl !== '') {
            window.location.href = this.innerSubmitRedirectUrl;
          }
          this.isShowSubmitted = true;
        } catch (error: any) {
          if (this.innerErrorElementId !== undefined && this.innerErrorElementId !== '') {
            const el = document.getElementById(this.innerErrorElementId);
            if (el === null) {
              this.$log.warning(`Element with id '${this.innerErrorElementId}' not found.`);
            } else {
              el.classList.add('glitWidgetElementVisible');
            }
          } else { this.errorResponse = error; }
        }
        this.isSubmitting = false;
      },
      async loadFormDefinition() {
        this.isLoading = true;
        try {
          const req = new GetDynamicFormDefinitionRequestViewModel();
          req.appSystemKey = this.innerAppSystemKey;
          req.id = this.innerDefinitionId;
          req.businessUnit = this.innerBusinessUnit;
          this.errorResponse = undefined;
          this.formDefinition = await this.$service.api.core.dynamicForm.getDynamicFormDefinition(req);
        } catch (error: any) {
          this.errorResponse = error;
        }
        this.isLoading = false;
      },
      async initializeFormPayload() {
        this.isLoading = true;
        try {
          const req = new GetDynamicFormPayloadRequestModel();
          req.formDefinitionId = this.innerDefinitionId;
          req.contactId = this.innerContactId;
          req.appSystemKey = this.innerAppSystemKey;
          const payload = await Vue.$service.v2.api.kfmvDynamicForm.getDynamicFormPayload(req);
          const payloadData = payload.data;
          payloadData.contactFormType = this.formPayload.contactFormType;
          this.formPayload = new DynamicFormPayloadResourceViewModel().fromDTO(payloadData as any);
          // Format the phone and mobile phone numbers, like the <p-phone-field> does, to avoid validation errors (Bug #21158)
          if (this.formPayload.contact?.phoneNumber) {
            this.formPayload.contact.phoneNumber = this.$format.phoneData(this.formPayload.contact.phoneNumber);
          }
          if (this.formPayload.contact?.mobilePhoneNumber) {
            this.formPayload.contact.mobilePhoneNumber = this.$format.phoneData(this.formPayload.contact.mobilePhoneNumber);
          }
          this.formPayload.skipRequestSummaryEmail = this.innerSkipRequestSummaryEmail;
        } catch (error: any) {
          this.errorResponse = error;
        }
        this.isLoading = false;
      },
    },
  });
</script>
